import React from 'react';
import {
  ArrowRight,
  CheckCircle2,
  CircleCheck,
  Film,
  FolderOpen,
  Layers3,
  PlayCircle,
  Quote,
  Rocket,
  Sparkles,
  Subtitles,
  XCircle,
} from 'lucide-react';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { bonuses, productData, resultVideos, testimonials } from '../data/editorPremium';

const CHECKOUT_URL = 'https://pay.kiwify.com.br/8ML4rCe';

const mainBenefits = [
  {
    icon: Rocket,
    title: 'Legenda pronta em minutos',
    description:
      'Escolha um template, aplique com 1 clique e a legenda já está no seu vídeo. Sem complicação, sem montar do zero.',
  },
  {
    icon: Layers3,
    title: 'Seus vídeos com a mesma cara sempre',
    description:
      'Use os mesmos templates em todos os seus projetos e entregue um resultado que parece profissional desde o primeiro vídeo.',
  },
  {
    icon: FolderOpen,
    title: 'Tudo no lugar, fácil de achar',
    description:
      'Seus templates ficam organizados dentro do plugin, com preview para visualizar antes de aplicar. Chega de pasta perdida.',
  },
  {
    icon: Film,
    title: 'Funciona no After Effects e no Premiere Pro',
    description:
      'Não importa qual programa você usa — o plugin funciona nos dois e mantém o mesmo fluxo de trabalho.',
  },
  {
    icon: Subtitles,
    title: 'Importe legendas SRT com 1 clique',
    description:
      'Tem um arquivo de legenda SRT? Importe direto no plugin e ele já cria tudo animado pro você.',
  },
];

const includedItems = [
  'Painel integrado para Adobe After Effects',
  'Painel integrado para Adobe Premiere Pro',
  'Biblioteca local com templates, previews e assets',
  'Fluxo com Motion Graphics Templates (.mogrt)',
  'Importação de arquivos SRT',
  'Presets e rotinas auxiliares',
  'Atualizações da estrutura atual do produto',
  'Acesso ao ecossistema Editor Premium',
];

const CourseView = () => {
  // Página de vendas do Plugin de Legendas — aberta a todos os usuários
  return (
    <div className="bg-[#050505] text-white selection:bg-orange-500 selection:text-white -mx-4 md:-mx-8 -mt-6">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10 bg-[#050505]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-0 lg:grid-cols-2">
            <div className="py-12 lg:py-20 lg:pr-10">
              <div className="inline-flex items-center rounded-md border border-orange-500/30 bg-orange-500/10 px-4 py-1 text-xs font-semibold text-orange-300">
                Editor Premium Legendas
              </div>

              <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight text-white md:text-5xl">
                Pare de perder horas editando legenda. Faça em minutos com o Editor Premium.
              </h1>

              <p className="mt-6 text-lg leading-relaxed text-zinc-300">
                Um plugin para ganhar tempo e acelerar a tua edição — com mais de 35 textos animados prontos, presets, SRT e MOGRT para AE e Premiere Pro.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <a
                  href="#oferta"
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-8 py-4 text-base font-black text-white transition hover:scale-[1.02] hover:bg-orange-400"
                >
                  Quero editar mais rápido →
                </a>
                <a
                  href="#demonstracao"
                  className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-8 py-4 text-base font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
                >
                  Ver como funciona
                </a>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-zinc-300">
                {[
                  '+35 textos animados prontos para usar',
                  'Aplica com 1 clique no AE e no Premiere',
                  'Importa arquivo SRT automaticamente',
                  'Acesso anual + atualizações do ecossistema',
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
                  >
                    <span className="text-orange-400">✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative order-first mx-auto w-full max-w-md sm:max-w-lg lg:order-last lg:max-w-none lg:py-8">
              <img
                src="https://midia.packcinematografico.com/Pagina%20de%20vendas/hero-new-700.webp"
                srcSet="https://midia.packcinematografico.com/Pagina%20de%20vendas/hero-new-400.webp 400w, https://midia.packcinematografico.com/Pagina%20de%20vendas/hero-new-700.webp 700w, https://midia.packcinematografico.com/Pagina%20de%20vendas/hero-new-834.webp 834w"
                sizes="(max-width: 640px) 448px, (max-width: 1024px) 512px, 600px"
                alt="Editor Premium Legendas — plugin para After Effects e Premiere Pro"
                className="mx-auto block h-auto w-full object-contain"
                loading="eager"
                width="834"
                height="868"
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: [
                    'linear-gradient(to right, #050505 0%, transparent 22%)',
                    'linear-gradient(to left, #050505 0%, transparent 8%)',
                    'linear-gradient(to bottom, #050505 0%, transparent 8%)',
                    'linear-gradient(to top, #050505 0%, transparent 20%)',
                  ].join(', '),
                }}
              />
            </div>
          </div>

          {/* VSL */}
          <div className="mx-auto max-w-4xl py-12">
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
              <video
                src="https://midia.packcinematografico.com/Pagina%20de%20vendas/VSL.mp4"
                controls
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Card de benefícios */}
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="my-10 overflow-hidden rounded-[28px] border border-white/10 bg-zinc-950/80 shadow-[0_30px_120px_rgba(0,0,0,0.5)]">
              <div className="flex flex-col justify-center p-6 lg:p-8">
                <div className="inline-flex w-fit items-center rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-sm font-semibold text-orange-300">
                  Por que editores usam o Editor Premium
                </div>
                <h2 className="mt-5 text-2xl font-bold text-white md:text-3xl">
                  Você demora horas fazendo o que deveria levar minutos
                </h2>
                <p className="mt-4 text-base leading-relaxed text-zinc-300">
                  Editores iniciantes perdem um tempo enorme criando legenda do zero em cada vídeo. Com o Editor Premium você escolhe um template pronto, aplica com 1 clique e segue para o próximo projeto.
                </p>
                <div className="mt-6 space-y-3 text-sm text-zinc-300">
                  {[
                    '+35 animações de legenda prontas para usar agora',
                    'Veja como fica antes de aplicar',
                    'Aplica direto no seu projeto com 1 clique',
                    'Importa legenda SRT sem digitar nada',
                    'Quanto mais você usa, mais rápido fica',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CircleCheck className="mt-0.5 h-5 w-5 flex-none text-orange-400" aria-hidden="true" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4 rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5">
                  <div className="text-center">
                    <p className="text-2xl font-black text-orange-400">4h+</p>
                    <p className="mt-1 text-xs text-zinc-400">economizadas por semana</p>
                  </div>
                  <div className="text-center border-x border-white/10">
                    <p className="text-2xl font-black text-orange-400">+35</p>
                    <p className="mt-1 text-xs text-zinc-400">templates prontos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-orange-400">1 clique</p>
                    <p className="mt-1 text-xs text-zinc-400">para aplicar qualquer legenda</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFÍCIOS PRINCIPAIS */}
      <section className="border-b border-white/10 bg-zinc-950 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="border border-orange-500/20 bg-orange-500/10 text-orange-300">
              Principais benefícios
            </Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Por que o Editor Premium muda sua edição
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {mainBenefits.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="border border-white/10 bg-black text-white">
                <CardContent className="p-6">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-400">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-300">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="border-b border-white/10 bg-black py-20" id="problema">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="border border-orange-500/20 bg-orange-500/10 text-orange-300">O problema</Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Sem o plugin, legenda consome seu tempo todo
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-zinc-300">
              Se você já editou vídeos com legenda sabe: o problema não é gravar ou cortar. É criar a mesma animação de legenda do zero em todo vídeo que chega.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Card className="border border-white/10 bg-white/[0.03] text-white">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <XCircle className="h-6 w-6 text-orange-400" />
                  <h3 className="text-xl font-bold">Sem o Editor Premium</h3>
                </div>
                <div className="space-y-3 text-zinc-300">
                  {productData.withoutPack.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <XCircle className="mt-0.5 h-5 w-5 flex-none text-orange-400" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-orange-500/15 bg-orange-500/5 text-white">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-orange-400" />
                  <h3 className="text-xl font-bold">Com o Editor Premium</h3>
                </div>
                <div className="space-y-3 text-zinc-200">
                  {productData.withPack.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-orange-400" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* DEMONSTRAÇÃO */}
      <section className="border-b border-white/10 bg-zinc-950 py-20" id="demonstracao">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="border border-white/10 bg-white/5 text-zinc-200">Demonstração</Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Veja alguns resultados que esse fluxo entrega
            </h2>
            <p className="mt-4 text-lg text-zinc-300">
              A ideia não é só ter templates. É operar com um sistema que acelera produção de verdade.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {resultVideos.map((video) => (
              <Card key={video.id} className="overflow-hidden border border-white/10 bg-black text-white">
                <div className="aspect-[9/16] bg-zinc-900">
                  <video
                    className="h-full w-full object-cover"
                    src={video.videoUrl}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
                <CardContent className="p-5">
                  <div className="inline-flex items-center rounded-full bg-orange-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-300">
                    {video.tag}
                  </div>
                  <h3 className="mt-3 text-xl font-bold">{video.title}</h3>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16">
            <div className="mx-auto mb-8 max-w-3xl text-center">
              <Badge className="border border-orange-500/20 bg-orange-500/10 text-orange-300">
                Previews reais
              </Badge>
              <h3 className="mt-4 text-2xl font-black text-white md:text-4xl">
                Alguns dos textos animados que você terá acesso
              </h3>
              <p className="mt-3 text-zinc-300">
                Com apenas alguns cliques você terá esses títulos nas suas edições dentro do Premiere e After Effects
              </p>
            </div>

            <div className="mx-auto max-w-4xl">
              <div className="aspect-video overflow-hidden rounded-xl border border-white/10">
                <video
                  src="https://midia.packcinematografico.com/desfoque.mp4"
                  className="h-full w-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* O QUE VOCÊ RECEBE */}
      <section className="border-b border-white/10 bg-black py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="border border-orange-500/20 bg-orange-500/10 text-orange-300">
              O que você recebe
            </Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Tudo que você precisa para criar legendas profissionais — mesmo começando do zero
            </h2>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-2">
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-zinc-950/80 p-8 shadow-2xl transition-all hover:border-blue-500/30">
              <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="lg:pr-8">
                  <span className="inline-block rounded-md border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-400">
                    Premiere Pro
                  </span>
                  <h3 className="mt-4 text-3xl font-black text-white">+35 Templates prontos</h3>
                  <p className="mt-2 text-base text-zinc-400">Em cada template, você controla tudo:</p>
                  <ul className="mt-6 space-y-3.5">
                    {['Cor das palavras', 'Fonte', 'Posição', 'Escala', 'Rotação'].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-lg text-zinc-200">
                        <span className="text-orange-400">✦</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mx-auto w-full max-w-[220px] shrink-0 lg:mx-0">
                  <img
                    src="https://midia.packcinematografico.com/Pagina%20de%20vendas/Captura%20de%20tela%202026-03-27%20093754.png"
                    alt="Preview do plugin no Premiere Pro"
                    className="h-auto w-full rounded-xl object-contain transition-transform duration-300 hover:scale-105"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-white/10 bg-zinc-950/80 p-8 shadow-2xl transition-all hover:border-purple-500/30">
              <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="lg:pr-8">
                  <span className="inline-block rounded-md border border-purple-500/20 bg-purple-500/10 px-3 py-1 text-xs font-semibold text-purple-400">
                    After Effects
                  </span>
                  <h3 className="mt-4 text-3xl font-black text-white">+35 Textos animados e ferramentas</h3>
                  <p className="mt-2 text-base text-zinc-400">O fluxo mais rápido para AE:</p>
                  <ul className="mt-6 space-y-3.5">
                    {[
                      'Zooms animados',
                      'Sólidos animados',
                      'Presets de animação',
                      'Decompor texto',
                      'Extrator de pré-composição',
                      'Empilhar textos',
                      'Importador de SRT',
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-lg text-zinc-200">
                        <span className="text-orange-400">✦</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mx-auto w-full max-w-[220px] shrink-0 lg:mx-0">
                  <img
                    src="https://midia.packcinematografico.com/Pagina%20de%20vendas/Captura%20de%20tela%202026-03-27%20093720.png"
                    alt="Preview do plugin no After Effects"
                    className="h-auto w-full rounded-xl object-contain transition-transform duration-300 hover:scale-105"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 rounded-[28px] border border-white/10 bg-white/5 p-8 shadow-inner">
            <h3 className="text-2xl font-bold text-white">Incluso no Editor Premium</h3>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {includedItems.map((item) => (
                <div key={item} className="flex items-start gap-3 text-base text-zinc-200">
                  <CheckCircle2 className="mt-0.5 h-6 w-6 flex-none text-orange-400" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BÔNUS */}
      <section className="border-b border-white/10 bg-zinc-950 py-20" id="bonus">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="border border-yellow-500/20 bg-yellow-500/10 text-yellow-300">Bônus</Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              E ainda ganhe 3 bônus exclusivos para turbinar suas edições.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {bonuses.map((bonus) => (
              <Card key={bonus.title} className="overflow-hidden border border-white/10 bg-black text-white">
                <div className="aspect-square overflow-hidden bg-zinc-900">
                  <img src={bonus.image} alt={bonus.title} className="h-full w-full object-contain" />
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-orange-300">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-sm font-semibold uppercase tracking-wide">Extra</span>
                  </div>
                  <h3 className="mt-3 text-lg font-bold">{bonus.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-300">{bonus.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* OFERTA */}
      <section className="border-b border-white/10 bg-black py-24" id="oferta">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <div className="inline-flex items-center rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1 text-xs font-semibold text-orange-300 uppercase tracking-widest">
              Oferta de Lançamento
            </div>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Tudo que você precisa para<br />legendar rápido e de verdade
            </h2>
            <p className="mt-3 text-zinc-400 text-base">
              Se fosse comprar cada parte separado custaria R$ 459. Aqui você leva tudo junto por:
            </p>
          </div>

          <div className="relative overflow-hidden rounded-[28px] border border-orange-500/40 bg-[#0f0f0f] shadow-[0_0_60px_rgba(249,115,22,0.15)]">
            <div className="absolute -top-px left-1/2 -translate-x-1/2">
              <div className="rounded-b-xl bg-orange-500 px-6 py-1.5 text-xs font-black uppercase tracking-widest text-white shadow-lg">
                🔥 Acesso Imediato
              </div>
            </div>

            <div className="px-6 pb-10 pt-14 md:px-12">
              <div className="mb-8 text-center">
                <p className="text-sm font-semibold uppercase tracking-widest text-orange-400">
                  Editor Premium Legendas
                </p>
                <p className="mt-1 text-zinc-400 text-sm italic">
                  Para editores que querem parar de perder tempo com legenda
                </p>
              </div>

              <div className="mb-8 space-y-0 divide-y divide-white/5 rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">
                {[
                  { icon: '⚡', label: 'Plugin Editor Premium Legendas — After Effects', price: 'R$ 97', bonus: false },
                  { icon: '⚡', label: 'Plugin Editor Premium Legendas — Premiere Pro', price: 'R$ 97', bonus: false },
                  { icon: '⚡', label: 'Importação de SRT + Fluxo MOGRT', price: 'R$ 37', bonus: false },
                  { icon: '⚡', label: 'Presets e Rotinas Auxiliares', price: 'R$ 47', bonus: false },
                  { icon: '★', label: 'Bônus: Treinamento Textos Imersivos', price: 'R$ 67', bonus: true },
                  { icon: '★', label: 'Bônus: Treinamento Legendas Dinâmicas', price: 'R$ 67', bonus: true },
                  { icon: '★', label: 'Bônus: Scripts de Automação — After Effects', price: 'R$ 47', bonus: true },
                  { icon: '★', label: 'Bônus: Pack de Efeitos Sonoros', price: 'R$ 37', bonus: true },
                  { icon: '★', label: 'Bônus: Pack de Overlays Visuais', price: 'R$ 37', bonus: true },
                  { icon: '⚡', label: 'Atualização de Templates', price: 'R$ 37', bonus: false },
                  { icon: '⚡', label: 'Acesso a comunidade com mais de 200 editores', price: 'imensurável', bonus: false },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-5 py-3.5 ${row.bonus ? 'bg-orange-500/5' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-lg ${row.bonus ? 'text-yellow-400' : 'text-orange-500'}`}>
                        {row.icon}
                      </span>
                      <span className="text-sm font-semibold text-white">{row.label}</span>
                    </div>
                    <span className="text-sm font-bold text-orange-400 line-through opacity-70">
                      {row.price}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mb-6 text-center">
                <p className="text-sm text-zinc-500">
                  De <span className="text-zinc-400 line-through">R$ 459,00</span> você investe somente...
                </p>
              </div>

              <div className="text-center">
                <p className="text-sm font-semibold text-green-400 mb-1">12x de R$</p>
                <div className="text-6xl font-black text-green-500 md:text-7xl leading-none">
                  20
                  <span className="text-3xl font-bold text-green-400">,37</span>
                </div>
                <p className="mt-2 text-sm text-zinc-400">ou R$ 197,00 à vista</p>
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                <a
                  href={CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center rounded-full bg-green-500 px-10 py-5 text-lg font-black text-white transition hover:scale-[1.02] hover:bg-green-400 shadow-[0_0_30px_rgba(34,197,94,0.4)]"
                >
                  EU QUERO O PLUGIN
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>

                <div className="mt-2 flex w-full flex-col items-center gap-3 rounded-2xl border border-green-500/20 bg-green-500/5 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full border-2 border-green-500/40 bg-green-500/10">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-base font-black text-green-400">Garantia incondicional de 7 dias</p>
                      <p className="mt-0.5 text-xs text-zinc-400">Se não gostar, devolvemos 100% do seu dinheiro. Sem burocracia.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3 text-xs text-zinc-500">
                    <span className="flex items-center gap-1"><span className="text-green-500">✓</span> Acesso Anual</span>
                    <span className="flex items-center gap-1"><span className="text-green-500">✓</span> Pagamento pela Kiwify</span>
                    <span className="flex items-center gap-1"><span className="text-green-500">✓</span> Licença por ativação</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROVA SOCIAL */}
      <section className="border-b border-white/10 bg-zinc-950 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="border border-white/10 bg-white/5 text-zinc-200">Prova social</Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Gente real usando e aprovando
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item) => (
              <Card key={item.image} className="overflow-hidden border border-white/10 bg-black text-white">
                <div className="relative">
                  <img src={item.image} alt={item.alt} className="h-full w-full object-cover" />
                  <div className="absolute left-4 top-4 rounded-full bg-black/70 p-2 text-orange-300">
                    <Quote className="h-4 w-4" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-white/10 bg-black py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <div>
            <Badge className="border border-orange-500/20 bg-orange-500/10 text-orange-300">FAQ</Badge>
            <h2 className="mt-5 text-3xl font-black text-white md:text-5xl">
              Dúvidas comuns antes da compra
            </h2>
            <p className="mt-4 text-lg text-zinc-300">
              A ideia aqui é deixar claro o que o produto entrega hoje e para quem ele faz mais sentido.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {productData.faqs.map((faq, index) => (
              <AccordionItem
                key={faq.question}
                value={`item-${index}`}
                className="rounded-2xl border border-white/10 bg-zinc-950 px-6"
              >
                <AccordionTrigger className="text-left text-base font-bold text-white hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-base leading-relaxed text-zinc-300">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* QUEM CRIOU */}
      <section className="bg-zinc-950 py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Badge className="border border-white/10 bg-white/5 text-zinc-200">Quem criou</Badge>
          <div className="mt-8 flex flex-col items-center">
            <img
              src={productData.about.imageUrl}
              alt={productData.about.name}
              className="h-28 w-28 rounded-full border border-white/10 object-cover"
            />
            <h2 className="mt-5 text-3xl font-black text-white">{productData.about.name}</h2>
            <p className="mt-5 text-lg leading-relaxed text-zinc-300">
              {productData.about.description}
            </p>
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-orange-500 px-8 py-4 text-base font-black text-white transition hover:scale-[1.02] hover:bg-orange-400"
            >
              Quero editar mais rápido agora
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-black py-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 text-sm text-zinc-400 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-3 text-zinc-300">
            <PlayCircle className="h-5 w-5 text-orange-400" />
            <span className="font-semibold">Editor Premium Legendas</span>
          </div>
          <div>After Effects + Premiere Pro | Fluxo pensado para produtividade real</div>
        </div>
      </footer>
    </div>
  );
};

export default CourseView;
